import AssetsList from '../assets/AssetsListV2';
import AssetSelectorPanel from '../issues/AssetSelectorPanel';
import { useBoolean } from '@fluentui/react-hooks';
import { editOrder } from '@/services/orderService';
import { useMemo, useState } from 'react';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  messageBarAtom,
  successMessageAtom,
} from '@/atoms/messageBarAtoms';
import ConfirmDialog from '../../common/ConfirmDialog';
import { MessageBarType, Panel, PanelType } from '@fluentui/react';
import AssetDetailsV2 from '../assets/AssetDetailsV2';
import {
  Button,
  MessageBar,
  MessageBarBody,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { assetRelationshipLabels, isAssetFormAllowed } from '@moko/utils';
import type { useWorkAssetForms } from '@moko/ui';
import ImportAsset from '../import/ImportAsset';
import type { IImportedAsset } from '../../services/importService';

type PendingAssetAction = {
  type: 'add' | 'remove';
  assetId: number;
  assetCode?: string;
  assetName?: string;
  assetUuid?: string;
  formDefinitionId?: number;
};

export interface IOrderAssetsProps {
  onAssetFormResolved: (assetId: number, formDefinitionId: number) => void;
  assetForms: ReturnType<typeof useWorkAssetForms>;
  tenantId?: number;
  orderId?: number;
  assetIds?: number[];
  onAssetIdsChanged?: (assetIds: number[]) => void;
}

const OrderAssets = ({
  assetForms,
  tenantId,
  onAssetFormResolved,
  orderId,
  assetIds,
  onAssetIdsChanged,
}: IOrderAssetsProps) => {
  const labels = assetRelationshipLabels(assetForms.relationships);
  const allowedFormIds = useMemo(
    () => assetForms.relationships.map((link) => link.assetFormDefinitionId),
    [assetForms.relationships]
  );
  const requireRestrictions = (action: () => void) => {
    if (!assetForms.ready) {
      setErrorMessage(
        assetForms.message ?? 'Load permitted Asset forms first.'
      );
      return;
    }
    action();
  };
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setMessageBar = useSetAtom(messageBarAtom);

  const [
    isAssetSelectorOpen,
    { setTrue: showAssetSelector, setFalse: hideAssetSelector },
  ] = useBoolean(false);

  const [isConfirmOpen, { setTrue: showConfirm, setFalse: hideConfirm }] =
    useBoolean(false);
  const [
    isCreateAssetPanelOpen,
    { setTrue: showCreateAssetPanel, setFalse: hideCreateAssetPanel },
  ] = useBoolean(false);
  const [
    isImportAssetPanelOpen,
    { setTrue: showImportAssetPanel, setFalse: hideImportAssetPanel },
  ] = useBoolean(false);
  const [pendingAction, setPendingAction] = useState<PendingAssetAction | null>(
    null
  );

  const confirmMessage = useMemo(() => {
    if (!pendingAction) return '';
    if (pendingAction.type === 'remove') {
      return `The asset with id ${pendingAction.assetId} will be removed from the Order.`;
    }

    const label =
      pendingAction.assetCode && pendingAction.assetName
        ? `${pendingAction.assetCode}|${pendingAction.assetName}`
        : pendingAction.assetId;
    return `Asset "${label}" will be added to the Order.`;
  }, [pendingAction]);

  const updateRelatedAssets = async (
    nextAssetIds: number[],
    messages: { persisted: string; draft: string },
    options?: { hideSelector?: boolean; createdFormId?: number }
  ): Promise<boolean> => {
    if (!orderId) {
      onAssetIdsChanged?.(nextAssetIds);
      setMessageBar(messages.draft, MessageBarType.info);
      if (options?.hideSelector) {
        hideAssetSelector();
      }
      return true;
    }
    try {
      if (
        nextAssetIds.some((id) => !(assetIds ?? []).includes(id)) &&
        (!assetForms.ready ||
          !isAssetFormAllowed(assetForms.relationships, options?.createdFormId))
      )
        throw new Error(
          assetForms.ready
            ? 'This Asset form is not permitted for the selected Work form.'
            : assetForms.message
        );
      setIsInProgress(true);
      const abortController = new AbortController();
      await editOrder(abortController, orderId, {
        relatedAssetIds: nextAssetIds,
      });
      onAssetIdsChanged?.(nextAssetIds);
      setSuccessMessage(messages.persisted);
      if (options?.hideSelector) {
        hideAssetSelector();
      }
      return true;
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
      return false;
    } finally {
      setIsInProgress(false);
    }
  };

  const removeAsset = async (assetId: number): Promise<void> => {
    const nextAssetIds = (assetIds ?? []).filter((id) => id !== assetId);
    await updateRelatedAssets(nextAssetIds, {
      persisted: 'Asset removed from Order successfully.',
      draft: 'Asset removed from draft. Save Order to persist changes.',
    });
  };

  const handleAssetRemoveClick = (assetId: number): void => {
    setPendingAction({ type: 'remove', assetId });
    showConfirm();
  };

  const addAsset = async (
    assetId: number,
    _assetCode: string,
    _assetName: string,
    _assetUuid: string,
    createdFormId?: number
  ): Promise<boolean> => {
    const nextAssetIds = (assetIds ?? []).includes(assetId)
      ? assetIds ?? []
      : [...(assetIds ?? []), assetId];
    return await updateRelatedAssets(
      nextAssetIds,
      {
        persisted: 'Asset added to Order successfully.',
        draft: 'Asset added to draft. Save Order to persist changes.',
      },
      { hideSelector: true, createdFormId }
    );
  };

  const handleAssetAddClick = async (
    assetId: number,
    assetCode: string,
    assetName: string,
    assetUuid: string,
    formDefinitionId?: number
  ): Promise<void> => {
    setPendingAction({
      type: 'add',
      assetId,
      assetCode,
      assetName,
      assetUuid,
      formDefinitionId,
    });
    if (formDefinitionId != null)
      onAssetFormResolved(assetId, formDefinitionId);
    showConfirm();
  };

  const handleConfirm = async (): Promise<void> => {
    const action = pendingAction;
    hideConfirm();
    setPendingAction(null);
    if (!action) return;

    if (action.type === 'remove') {
      await removeAsset(action.assetId);
      return;
    }

    await addAsset(
      action.assetId,
      action.assetCode ?? '',
      action.assetName ?? '',
      action.assetUuid ?? '',
      action.formDefinitionId
    );
  };

  const handleAssetsImported = async (
    importedAssets: IImportedAsset[]
  ): Promise<void> => {
    const importedAssetIds = Array.from(
      new Set(
        importedAssets
          .map((asset) => asset.id)
          .filter(
            (assetId): assetId is number =>
              typeof assetId === 'number' && assetId > 0
          )
      )
    );

    const newImportedAssetIds = importedAssetIds.filter(
      (assetId) => !(assetIds ?? []).includes(assetId)
    );

    if (newImportedAssetIds.length === 0) {
      hideImportAssetPanel();
      return;
    }

    onAssetIdsChanged?.([...(assetIds ?? []), ...newImportedAssetIds]);
    setSuccessMessage('Imported assets linked to Order successfully.');
    hideImportAssetPanel();
  };

  return (
    <>
      {!assetForms.ready && (
        <MessageBar intent={assetForms.error ? 'error' : 'info'}>
          <MessageBarBody>
            {assetForms.message}{' '}
            <Button onClick={assetForms.retry}>Retry</Button>
          </MessageBarBody>
        </MessageBar>
      )}
      {assetForms.issues.length > 0 && (
        <MessageBar intent="error">
          <MessageBarBody>
            <ul>
              {assetForms.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </MessageBarBody>
        </MessageBar>
      )}
      <AssetsList
        isInWorkContext={true}
        contextTabTitle={
          labels.plural ? `Selected ${labels.plural}` : 'Order Assets'
        }
        contextBackPath="/orders"
        assetIds={assetIds}
        onAddAssetClick={() => requireRestrictions(showAssetSelector)}
        onRemoveAssetClick={handleAssetRemoveClick}
        onCreateAssetClick={() => requireRestrictions(showCreateAssetPanel)}
        onImportAssetClick={() => {
          if (!orderId) {
            setErrorMessage('Save Order before importing assets.');
            return;
          }
          requireRestrictions(showImportAssetPanel);
        }}
      />
      <AssetSelectorPanel
        isOpen={isAssetSelectorOpen && assetForms.ready}
        allowedFormIds={allowedFormIds}
        tenantId={tenantId}
        hideAssetSelector={hideAssetSelector}
        selectorPanelHeaderText={
          labels.singular
            ? `Link existing ${labels.singular}`
            : 'Link existing asset to this order'
        }
        onAssetLinkClick={handleAssetAddClick}
      />
      <Panel
        isOpen={isCreateAssetPanelOpen && assetForms.ready}
        onDismiss={hideCreateAssetPanel}
        closeButtonAriaLabel="Close"
        type={PanelType.custom}
        customWidth="550px"
        headerText={
          labels.singular ? `Add ${labels.singular}` : 'Create new asset'
        }
      >
        {isCreateAssetPanelOpen ? (
          <FluentProvider theme={webLightTheme}>
            <AssetDetailsV2
              relationships={assetForms.relationships}
              tenantId={tenantId}
              itemIdOverride={0}
              backPath=""
              onCancel={hideCreateAssetPanel}
              onAssetCreated={async (createdAsset) => {
                if (createdAsset.formDefinitionId != null)
                  onAssetFormResolved(
                    createdAsset.id,
                    createdAsset.formDefinitionId
                  );
                const linked = await addAsset(
                  createdAsset.id,
                  createdAsset.code,
                  createdAsset.name,
                  createdAsset.uuid,
                  createdAsset.formDefinitionId
                );
                if (linked) {
                  hideCreateAssetPanel();
                }
              }}
            />
          </FluentProvider>
        ) : null}
      </Panel>
      <Panel
        isOpen={isImportAssetPanelOpen && assetForms.ready}
        onDismiss={hideImportAssetPanel}
        closeButtonAriaLabel="Close"
        type={PanelType.custom}
        customWidth="550px"
        headerText={labels.plural ? `Import ${labels.plural}` : 'Import assets'}
      >
        {isImportAssetPanelOpen ? (
          <ImportAsset
            relationships={assetForms.relationships}
            tenantId={tenantId}
            onAssetsImported={handleAssetsImported}
            linkEntity={
              orderId ? { entityType: 'Order', entityId: orderId } : undefined
            }
          />
        ) : null}
      </Panel>
      <ConfirmDialog
        isModalOpen={isConfirmOpen}
        hideModal={() => {
          hideConfirm();
          setPendingAction(null);
        }}
        message={confirmMessage}
        onYesClick={handleConfirm}
      />
    </>
  );
};

export default OrderAssets;
